// ============================================================================
// store/stash-scoped/categorySelectors.ts
//
// Pure view-model builder for the Category Detail screen.
//
// See docs/prd/stashboard-categories-prd.md.
//
// This module is intentionally framework-free so it can be exercised by pure
// unit tests; scope filtering is the caller's (hook's) responsibility — pass
// in `cards` already narrowed to the active scope.
// ============================================================================

import type { StashCard, StashCategory } from './types';

const MAX_PREVIEW_VENDOR_NAMES = 3;

export interface SubCategoryVM {
  id: string;
  name: string;
  /** Count of stash cards under this sub-category (and any descendants). */
  cardCount: number;
  /** Up to 3 distinct vendor names from those cards, for the row preview. */
  previewVendorNames: string[];
  /** Count of distinct friends who have a card in the subtree. 0 → hide the flag. */
  friendCount: number;
}

export interface CategoryDetailVM {
  parent: {
    id: string;
    name: string;
    imageUrl?: string;
    /** Human-readable HomeBase name for the breadcrumb (caller-supplied). */
    homeBaseName: string;
    /** All cards under the parent (including every descendant). */
    totalCardCount: number;
    /** Number of direct children — drives the hero summary line. */
    subCategoryCount: number;
    /** Distinct friends who have a card anywhere in the parent's subtree. */
    friendCount: number;
  };
  /** Direct children of the parent, sorted for display. */
  subCategories: SubCategoryVM[];
  /** True when the parent has no direct children (drives the page's dual-mode render). */
  isLeaf: boolean;
}

/**
 * Build the category detail VM for `categoryId`.
 *
 * `cards` should already be filtered to the active scope (All / Friends / Mine)
 * — the selector is pure and does not consult the store. Returns `null` when
 * the category id is not present in `categories`.
 *
 * Cycle-safe: a corrupt parent chain (A → B → A) won't blow the stack — the
 * descendant walk carries an ancestor `Set` and short-circuits on re-entry,
 * matching the guard in `buildCategoryTree` (pipeline.ts §3.6).
 */
export function buildCategoryDetail(
  categoryId: string,
  categories: StashCategory[],
  cards: StashCard[],
  _currentUserStasherId: string,
  friendIds: Set<string>,
  homeBaseName: string,
): CategoryDetailVM | null {
  const parent = categories.find(c => c.recordID === categoryId);
  if (!parent) return null;

  // childrenByParent — built once, reused for every descendant walk below.
  const childrenByParent = new Map<string, StashCategory[]>();
  for (const c of categories) {
    const pid = c.parentCategoryID ?? '';
    const list = childrenByParent.get(pid);
    if (list) list.push(c);
    else childrenByParent.set(pid, [c]);
  }

  // Inclusive descendant id set with a cycle-guard ancestor Set.
  const descendantIdsOf = (rootId: string): Set<string> => {
    const out = new Set<string>();
    const walk = (id: string, ancestors: Set<string>): void => {
      if (ancestors.has(id)) return;            // cycle — short-circuit
      out.add(id);
      const nextAncestors = new Set(ancestors).add(id);
      const kids = childrenByParent.get(id) ?? [];
      for (const kid of kids) walk(kid.recordID, nextAncestors);
    };
    walk(rootId, new Set());
    return out;
  };

  // A card belongs to a subtree when any of its vendor's categories falls in it.
  const cardsInSubtree = (rootId: string): StashCard[] => {
    const ids = descendantIdsOf(rootId);
    return cards.filter(card =>
      card.vendor.categories.some(cat => ids.has(cat.recordID)),
    );
  };

  const directChildren = childrenByParent.get(categoryId) ?? [];

  const subCategories: SubCategoryVM[] = directChildren.map(child => {
    const cardsHere = cardsInSubtree(child.recordID);

    // Up to N distinct vendor names, in encounter order, no duplicates.
    const previewVendorNames: string[] = [];
    const seenNames = new Set<string>();
    for (const card of cardsHere) {
      const name = card.vendor.companyName;
      if (!name || seenNames.has(name)) continue;
      seenNames.add(name);
      previewVendorNames.push(name);
      if (previewVendorNames.length === MAX_PREVIEW_VENDOR_NAMES) break;
    }

    const friendsHere = new Set<string>();
    for (const card of cardsHere) {
      if (friendIds.has(card.stasherId)) friendsHere.add(card.stasherId);
    }

    return {
      id: child.recordID,
      name: child.name,
      cardCount: cardsHere.length,
      previewVendorNames,
      friendCount: friendsHere.size,
    };
  });

  // Sort: empties always last; then by cardCount DESC; alphabetical tiebreak.
  // subCategories.sort((a, b) => {
  //   const aEmpty = a.cardCount === 0;
  //   const bEmpty = b.cardCount === 0;
  //   if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
  //   if (a.cardCount !== b.cardCount) return b.cardCount - a.cardCount;
  //   return a.name.localeCompare(b.name);
  // });

   subCategories.sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  // Parent rollups over the full subtree (inclusive of the parent itself).
  const parentSubtreeCards = cardsInSubtree(categoryId);
  const parentFriends = new Set<string>();
  for (const card of parentSubtreeCards) {
    if (friendIds.has(card.stasherId)) parentFriends.add(card.stasherId);
  }

  return {
    parent: {
      id: parent.recordID,
      name: parent.name,
      ...(parent.imageUrl !== undefined ? { imageUrl: parent.imageUrl } : {}),
      homeBaseName,
      totalCardCount: parentSubtreeCards.length,
      subCategoryCount: directChildren.length,
      friendCount: parentFriends.size,
    },
    subCategories,
    isLeaf: directChildren.length === 0,
  };
}
